import { useEffect, useState, useRef } from 'react';

//libs
import { z } from 'zod';
import { hexToRgbaWithOpacity } from '@/utils/hexToRgbaWithOpacity';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { useTranslation } from 'react-i18next';

//components
import { ExtraFieldsInputs, getExtraFieldsObject } from './extra-fields-inputs';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { ChevronDown, UserCheck } from 'lucide-react';
import CustomAlert from '@/components/Alert/Alert';
import CustomDatePicker from '@/components/DatePicker/DatePicker';
import { Input } from '@/components/@core/input';
import { Subtitle } from './subtitle';

//hooks
import { useConfig } from '@/hooks/useConfig';
import { QueryKeys } from '@/constants/queryKeys';
import { useDocumentType } from '@/pages/v2/profile/hooks/useDocumentType';
import { useProfilePage } from '@/pages/v2/profile/hooks/useProfilePage';

//utils
import { patch } from '@/services/api';
import { parseDate } from '@/selectors/date';
import { getCardSx } from '@/theme/utils';

const getInicialValue = (key: string): string => {
  const types = {
    'Cédula de ciudadanía': 'CEDULA',
    INE: 'INE',
    'Tarjeta de identidad': 'TI',
    Dni: 'DNI',
    'Cédula de extrajería': 'CEDULA_EX',
    'Permiso especial': 'PERMISO',
    'Salvo conducto de permanecia': 'SALVOC',
    Expendiente: 'EXPENDIENTE',
    Expediente: 'CPF',
    Pasaporte: 'PASS',
  };
  if (key.length === 0) {
    return types['Cédula de ciudadanía'];
  }
  // @ts-ignore
  return types[key] as string;
};

export const PersonalInformation = () => {
  const config = useConfig();
  const profile = useProfilePage(false);
  const document_types = useDocumentType();
  const theme = useTheme();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  // Refs for form fields to enable smooth scrolling to errors
  const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const Schema = z.object({
    first_name: z.string({
      message: `${t('general.fieldRequired')}`,
    }),
    last_name: z.string({
      message: `${t('general.fieldRequired')}`,
    }),
    friendly_name: z.string({
      message: `${t('general.fieldRequired')}`,
    }),
    email: z
      .string({
        message: `${t('general.fieldRequired')}`,
      })
      .email({
        message: `${t('general.isNotValidEmail')}`,
      }),
  });

  const formik = useFormik({
    initialValues: {
      first_name: profile.data?.first_name ?? '',
      last_name: profile.data?.last_name ?? '',
      friendly_name: profile.data?.profile.friendly_name ?? '',
      email: profile.data?.email ?? '',
      phone_number: profile.data?.phone_number ?? '',
      document_number: profile.data?.profile.document_number,
      document_type: getInicialValue(profile.data?.profile.document_type ?? ''),
      birthdate: profile.data?.profile.birthdate ?? '',
      extra_fields: {
        ...getExtraFieldsObject(
          profile.data?.profile.company_extra_fields ?? [],
          profile.data?.profile.extra_fields ?? {},
        ),
      },
    },
    onSubmit: () => {
      mutation.mutate();
      queryClient.invalidateQueries({ queryKey: [QueryKeys.PROFILE] });
    },
    validationSchema: toFormikValidationSchema(Schema),
    enableReinitialize: true,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      let birthdate = formik.values.birthdate ?? '';
      birthdate = parseDate(formik.values.birthdate);
      const response = await patch(`users/:user_pk/`, {
        first_name: formik.values.first_name,
        last_name: formik.values.last_name,
        friendly_name: formik.values.friendly_name,
        email: formik.values.email,
        document_number: formik.values.document_number,
        document_type: formik.values.document_type,
        birthdate: birthdate,
        extra_fields: formik.values.extra_fields,
      });
      return response;
    },
    onSuccess: (response) => {
      if (response.status === 200) {
        setShowAlert(true);
        setIsEditing(false);
        setTimeout(() => {
          setShowAlert(false);
        }, 2000);
      } else {
        const errors = {};
        const obj = Object.entries(response.data);
        obj?.map(([key, value]) => {
          // @ts-ignore
          errors[key] = value;
        });
        formik.setErrors(errors);
      }
    },
  });

  const onCancel = () => {
    setIsEditing(false);
    formik.resetForm();
  };

  const [showExtraFields, setShowExtraFields] = useState(false);
  useEffect(() => {
    if (profile.isSuccess) {
      setShowExtraFields(true);
    }
  }, [profile.isSuccess]);

  // Scroll to first error field with smooth animation
  useEffect(() => {
    const firstErrorKey = Object.keys(formik.errors)[0];
    if (
      firstErrorKey &&
      formik.submitCount > 0 &&
      fieldRefs.current[firstErrorKey]
    ) {
      fieldRefs.current[firstErrorKey]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [formik.errors, formik.submitCount]);

  const labelSx = {
    fontFamily: 'Inter, sans-serif',
    fontSize: theme.fontSizes.caption,
    color: theme.palette.text.secondary,
    display: 'block',
    mb: 0.5,
  };

  const readOnlyRowSx = {
    py: 1.5,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  };

  return (
    <Box sx={{ ...getCardSx(theme), cursor: 'default', mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Subtitle
          icon={<UserCheck size={18} color={theme.palette.primary.main} />}
          text={t('profileScreen.personalInformation')}
        />
        {!isEditing && (
          <Button
            size="small"
            onClick={() => setIsEditing(true)}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: theme.fontSizes.caption,
              fontWeight: 600,
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              borderRadius: '12px',
              textTransform: 'none',
              px: 1.5,
              minHeight: '32px',
              '&:hover': {
                bgcolor: hexToRgbaWithOpacity(theme.palette.primary.main, 0.06),
              },
              '&:active': { transform: 'scale(0.97)' },
            }}
            variant="outlined"
          >
            {t('general.edit')}
          </Button>
        )}
      </Box>

      {!isEditing && (
        <Box>
          {[
            {
              label: t('profileScreen.name'),
              value: profile.data?.first_name ?? '',
            },
            {
              label: t('profileScreen.lastname'),
              value: profile.data?.last_name ?? '',
            },
            {
              label: t('profileScreen.friendlyName'),
              value: profile.data?.profile.friendly_name ?? '',
            },
            {
              label: t('profileScreen.emailAddress'),
              value: profile.data?.email ?? '',
            },
            {
              label: t('profileScreen.phoneNumber'),
              value: profile.data?.phone_number ?? '',
            },
          ].map(({ label, value }) => (
            <Box key={label} sx={readOnlyRowSx}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: theme.fontSizes.caption,
                  color: theme.palette.text.secondary,
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: theme.fontSizes.body,
                  color: value
                    ? theme.palette.text.primary
                    : theme.palette.text.disabled,
                  mt: 0.25,
                }}
              >
                {value || '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {showAlert && (
        <Box sx={{ mb: 2 }}>
          <CustomAlert
            message={t('general.messageAfterSave')}
            severity={'success'}
            onClose={() => setShowAlert(false)}
          />
        </Box>
      )}

      <form
        onSubmit={formik.handleSubmit}
        style={{ display: isEditing ? 'block' : 'none' }}
      >
        <div ref={(el) => (fieldRefs.current['first_name'] = el)}>
          <Input
            disabled={config.restrict_name_certificate}
            style={{ marginBottom: '1rem' }}
            label={t('profileScreen.name')}
            inputProps={{
              type: 'text',
              ...formik.getFieldProps('first_name'),
            }}
            isError={!!formik.errors.first_name && formik.touched.first_name}
            errorText={formik.errors.first_name}
          />
        </div>
        <div ref={(el) => (fieldRefs.current['last_name'] = el)}>
          <Input
            disabled={config.restrict_name_certificate}
            style={{ marginBottom: '1rem' }}
            label={t('profileScreen.lastname')}
            inputProps={{
              type: 'text',
              ...formik.getFieldProps('last_name'),
            }}
            isError={!!formik.errors.last_name && formik.touched.last_name}
            errorText={formik.errors.last_name}
          />
        </div>
        <div ref={(el) => (fieldRefs.current['friendly_name'] = el)}>
          <Input
            style={{ marginBottom: '1rem' }}
            label={t('profileScreen.friendlyName')}
            inputProps={{
              type: 'text',
              ...formik.getFieldProps('friendly_name'),
            }}
            isError={
              !!formik.errors.friendly_name && formik.touched.friendly_name
            }
            errorText={formik.errors.friendly_name}
          />
        </div>
        <div ref={(el) => (fieldRefs.current['email'] = el)}>
          <Input
            style={{ marginBottom: '1rem' }}
            label={t('profileScreen.emailAddress')}
            inputProps={{
              type: 'text',
              ...formik.getFieldProps('email'),
            }}
            isError={!!formik.errors.email && formik.touched.email}
            errorText={formik.errors.email}
          />
        </div>
        <Input
          style={{ marginBottom: '1rem' }}
          label={t('profileScreen.phoneNumber')}
          inputProps={{
            disabled: true,
            type: 'text',
            ...formik.getFieldProps('phone_number'),
          }}
        />
        <div
          ref={(el) => (fieldRefs.current['birthdate'] = el)}
          style={{ marginBottom: '1rem' }}
        >
          <Typography component="label" sx={labelSx}>
            {t('profileScreen.dateBirth')}
          </Typography>
          <CustomDatePicker
            value={formik.values.birthdate}
            name="birthdate"
            onChange={formik.setFieldValue}
            format="dd/MM/yyyy"
          />
        </div>
        <div ref={(el) => (fieldRefs.current['document_type'] = el)}>
          <FormControl
            error={
              !!formik.errors.document_type && formik.touched.document_type
            }
            fullWidth
            sx={{ mb: 2 }}
          >
            <Typography component="label" sx={labelSx}>
              {t('profileScreen.documentType')}
            </Typography>
            <Select
              {...formik.getFieldProps('document_type')}
              value={
                document_types.isSuccess && document_types?.data
                  ? formik.getFieldProps('document_type').value
                  : ''
              }
              displayEmpty
              size="small"
              IconComponent={() => (
                <ChevronDown
                  size={18}
                  color={theme.palette.text.secondary}
                  style={{ marginRight: '8px', flexShrink: 0 }}
                />
              )}
              inputProps={{ 'aria-label': 'Without label' }}
              error={
                !!formik.errors.document_type && formik.touched.document_type
              }
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: theme.fontSizes.body,
                backgroundColor: 'white',
                borderRadius: '14px',
                color: theme.palette.text.primary,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                  borderRadius: '14px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${theme.palette.primary.main}60`,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '1.5px',
                },
              }}
            >
              <MenuItem
                disabled
                value=""
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: theme.fontSizes.body,
                }}
              >
                {t('general.selectOption')}
              </MenuItem>
              {document_types.isSuccess &&
                document_types?.data &&
                Object.entries(document_types?.data)?.map(([key, label]) => (
                  <MenuItem
                    key={key}
                    value={key}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: theme.fontSizes.body,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {/* @ts-ignore */}
                    {label}
                  </MenuItem>
                ))}
            </Select>
            <FormHelperText sx={{ fontFamily: 'Inter, sans-serif' }}>
              {formik.errors.document_type}
            </FormHelperText>
          </FormControl>
        </div>
        <div ref={(el) => (fieldRefs.current['document_number'] = el)}>
          <Input
            style={{ marginBottom: '1rem' }}
            label={t('profileScreen.documentNumber')}
            inputProps={{
              type: 'text',
              ...formik.getFieldProps('document_number'),
            }}
            isError={
              !!formik.errors.document_number && formik.touched.document_number
            }
            errorText={formik.errors.document_number}
          />
        </div>

        {showExtraFields && (
          <ExtraFieldsInputs
            extra_fields={profile.data?.profile.company_extra_fields ?? []}
            getFieldProps={formik.getFieldProps}
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, mt: 1 }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: theme.fontSizes.body,
              fontWeight: 500,
              borderRadius: '12px',
              height: '44px',
              textTransform: 'none',
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              minWidth: '100px',
              '&:hover': {
                borderColor: theme.palette.text.disabled,
                bgcolor: 'transparent',
              },
              '&:active': { transform: 'scale(0.97)' },
            }}
          >
            {t('general.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending}
            fullWidth
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: theme.fontSizes.body,
              fontWeight: 600,
              borderRadius: '12px',
              height: '44px',
              textTransform: 'none',
              bgcolor: theme.palette.primary.main,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
              '&:active': { transform: 'scale(0.97)' },
            }}
          >
            {mutation.isPending
              ? t('general.saving')
              : t('general.saveChanges')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
